import data from '@/data/sources/pulse/chart_ready.json';

// The text column mirrors the interactive Salutation (max 400px, 24px padding => 352px content),
// so the chart maps 1 SVG unit to 1px there and stays crisp.
const CONTENT_WIDTH = 352;
const CHART_COLUMNS = CONTENT_WIDTH / 2; // 1px bar + 1px gap per column
const BAR_HEIGHT = 18;
const TICK_GAP = 3;
const TICK_HEIGHT = 4;
const PRO_FILL = 'rgba(255, 255, 255, 0.9)';
const PERSONAL_FILL = 'rgba(255, 255, 255, 0.5)';

const ln1p = (x: number) => Math.log(1 + x);
const clamp = (x: number, lo: number, hi: number) =>
  Math.min(hi, Math.max(lo, x));

type Project = {
  category: 'professional' | 'personal';
  points: [number, number, number][];
};

function buildChart() {
  const projects = data.projects as unknown as Project[];
  const start = data.start ?? 0;
  const end = data.end ?? 1;
  const span = Math.max(1, end - start);
  const n = CHART_COLUMNS;

  const pro = new Array(n).fill(0);
  const per = new Array(n).fill(0);
  for (const p of projects) {
    const target = p.category === 'professional' ? pro : per;
    for (const [t, , w] of p.points) {
      target[clamp(Math.floor(((t - start) / span) * n), 0, n - 1)] += w;
    }
  }

  let maxStack = 1;
  for (let i = 0; i < n; i++)
    maxStack = Math.max(maxStack, ln1p(pro[i]) + ln1p(per[i]));
  const yScale = (BAR_HEIGHT - 1) / maxStack;

  const columns = Array.from({ length: n }, (_, i) => ({
    x: i * 2,
    pro: pro[i] > 0 ? Math.max(1, ln1p(pro[i]) * yScale) : 0,
    per: per[i] > 0 ? Math.max(1, ln1p(per[i]) * yScale) : 0,
  }));

  const ticks: number[] = [];
  for (let yr = 2014; yr <= 2026; yr++) {
    const t = Date.UTC(yr, 0, 1) / 1000;
    if (t < start || t > end) continue;
    ticks.push(clamp(Math.floor(((t - start) / span) * n), 0, n - 1) * 2);
  }

  return { width: n * 2, columns, ticks };
}

function PulseChart() {
  const { width, columns, ticks } = buildChart();
  const height = BAR_HEIGHT + TICK_GAP + TICK_HEIGHT;
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      shapeRendering="crispEdges"
      role="img"
      aria-label="Build activity per year, 2013 to now"
      style={{ width: '100%', height: 'auto', display: 'block', marginTop: 20 }}
    >
      {columns.map((c, i) => (
        <g key={i}>
          {c.per > 0 && (
            <rect
              x={c.x}
              y={BAR_HEIGHT - c.per}
              width={1}
              height={c.per}
              fill={PERSONAL_FILL}
            />
          )}
          {c.pro > 0 && (
            <rect
              x={c.x}
              y={BAR_HEIGHT - c.per - c.pro}
              width={1}
              height={c.pro}
              fill={PRO_FILL}
            />
          )}
        </g>
      ))}
      {ticks.map((x, i) => (
        <rect
          key={`t${i}`}
          x={x}
          y={BAR_HEIGHT + TICK_GAP}
          width={1}
          height={TICK_HEIGHT}
          fill="rgba(255, 255, 255, 0.5)"
        />
      ))}
    </svg>
  );
}

const linkStyle = {
  color: 'inherit',
  textDecoration: 'rgba(255, 255, 255, 0.5) underline',
  textUnderlineOffset: '0.2rem',
};

export function StaticLanding() {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: '#000',
        color: 'rgba(255, 255, 255, 0.92)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 400,
          padding: 24,
          boxSizing: 'border-box',
        }}
      >
        <pre
          style={{
            margin: 0,
            fontSize: '0.75rem',
            fontFamily: 'var(--font-supply-mono), monospace',
            lineHeight: 1.8,
            wordSpacing: '0.25em',
            whiteSpace: 'pre-wrap',
          }}
        >
          Ondřej Bárta{'\n\n'}
          Engineering leader, over a decade building software, 5+ years leading
          teams.{'\n\n'}
          I run a team at Glassnode, behind Studio, data viz, the public APIs
          and more.{'\n\n'}
          I built Bitwala&apos;s apps from scratch and grew with it past 500,000
          customers.{'\n\n'}
          I consult through Kilta, and ship my own things: TabPilot, featured by
          Apple, and GitPT, an on-device commit-message CLI.{'\n\n'}
          Full-stack, design-minded, hands on.{'\n\n'}→{' '}
          {/* oxlint-disable-next-line nextjs/no-html-link-for-pages -- links to a static PDF, not a page route */}
          <a href="/ondrej-barta-cv.pdf" style={linkStyle}>
            CV
          </a>
          {'   '}→{' '}
          <a href="https://github.com/bartaxyz" style={linkStyle}>
            GitHub
          </a>
          {'   '}→{' '}
          <a href="mailto:email@ondrejbarta.com" style={linkStyle}>
            Email
          </a>
        </pre>

        <PulseChart />
      </div>
    </div>
  );
}
